import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import './styles.css';
import './overall.css';
import NavRail from './NavRail';
import { TweaksPanel, TweakSection, TweakRadio, TweakToggle, useTweaks } from './TweaksPanel';
import { mergeDefaults } from './themePersist';
import {
  COMPANIES, PIVOT_ROWS, totalsRow, Company, PivotRow,
} from './overallData';

const TWEAK_DEFAULTS = {
  theme: 'paper' as 'paper' | 'light' | 'dark',
  density: 'regular' as 'compact' | 'regular' | 'comfy',
  variant: 'safe' as 'safe' | 'bold',
  diffOnly: false,
  showSparklines: true,
};

const fmt = (n: number | null | undefined): string => {
  if (n === null || n === undefined) return '—';
  const sign = n < 0 ? '−' : '';
  const abs = Math.abs(Math.round(n));
  return sign + abs.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
};

function TitleBar() {
  return (
    <div className="titlebar">
      <span className="brand">XELENCE</span>
      <span className="sep">/</span>
      <span className="crumb">Overall</span>
      <span className="sep">/</span>
      <span className="crumb"><b>Холдинг</b> · Q1 2026</span>
      <div className="right">
        <div className="stat"><span className="k">companies</span><span className="v">6</span></div>
        <div className="stat"><span className="k">period</span><span className="v">Q1 2026</span></div>
        <div className="stat"><span className="k">computed</span><span className="v">2 май 09:18</span></div>
      </div>
    </div>
  );
}

const PERIODS = ['Q1 2025', 'Q2 2025', 'Q3 2025', 'Q4 2025', 'Q1 2026', 'YTD'];

function Filters({ period, setPeriod }: { period: string; setPeriod: (v: string) => void }) {
  return (
    <div className="overall-filters">
      <div className="group">
        <span className="glbl">PERIOD</span>
        {PERIODS.map((p) => (
          <button key={p} className="pill" aria-pressed={period === p} onClick={() => setPeriod(p)}>
            {p}
          </button>
        ))}
      </div>
      <div className="right">
        <span>13 строк × 6 компаний · валюта <b>KZT</b></span>
      </div>
    </div>
  );
}

interface CellSelection {
  rowIdx: number;
  compId: string;
  row: PivotRow & { idx: number };
  comp: Company;
  val: number;
}

function Cell({ row, comp, val, selected, onSelect }: {
  row: PivotRow & { idx: number };
  comp: Company;
  val: number;
  selected: boolean;
  onSelect: (sel: CellSelection) => void;
}) {
  return (
    <div
      className={`cell ${selected ? 'selected' : ''}`}
      onClick={() => onSelect({ rowIdx: row.idx, compId: comp.id, row, comp, val })}
    >
      {fmt(val)}
    </div>
  );
}

function PivotTable({ rows, companies, selected, setSelected }: {
  rows: PivotRow[]; companies: Company[];
  selected: CellSelection | null;
  setSelected: (s: CellSelection) => void;
}) {
  return (
    <div className="pivot">
      <div className="ph">
        <div></div>
        <div className="first">Статья</div>
        {companies.map((c) => (
          <div key={c.id} className="co">
            <div className="short">{c.short}</div>
            <div className="full">{c.name}</div>
            <div className="meta" style={{ fontFamily: 'var(--mono)', fontSize: 9.5, color: 'var(--ink-4)' }}>
              {c.cluster}
            </div>
          </div>
        ))}
        <div className="totalh">
          ИТОГО ХОЛДИНГ
          <div className="meta">сумма по 6 компаниям</div>
        </div>
      </div>

      {rows.map((r, ri) => {
        const [tcalc] = totalsRow(r);
        return (
          <div key={ri} className={`pr l${r.level} ${r.total ? 'total' : ''}`}>
            <div className="lvl"></div>
            <div className="name">
              {r.level >= 1 && <span className="lvl-ind" />}
              {r.name.trim()}
            </div>
            {companies.map((c) => {
              const [v] = r.vals[c.id];
              const sel = !!selected && selected.rowIdx === ri && selected.compId === c.id;
              return (
                <Cell
                  key={c.id}
                  row={{ ...r, idx: ri }}
                  comp={c}
                  val={v}
                  selected={sel}
                  onSelect={setSelected}
                />
              );
            })}
            <div className="cell total-col">
              {fmt(tcalc)}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function DetailOverlay({ sel, onClose }: { sel: CellSelection | null; onClose: () => void }) {
  if (!sel) return null;
  const { row, comp, val } = sel;
  return (
    <div className="detail-overlay">
      <div className="dh">
        <span className="t">Cell · {comp.short} × {row.name.trim().slice(0, 24)}</span>
        <span className="x" onClick={onClose}>×</span>
      </div>
      <div className="body">
        <div className="row-title">{row.name.trim()}</div>
        <div className="co-line">{comp.name} · {comp.cluster}</div>
        <div className="row-val">{fmt(val)}</div>
        <div className="row-sub">расчётное значение · KZT</div>

        <div className="h">Расчёт</div>
        <div className="pl"><span className="l">cluster</span><span className="v">{comp.cluster}</span></div>
        <div className="pl"><span className="l">config</span><span className="v">full_sgd_v3</span></div>
        <div className="pl"><span className="l">period</span><span className="v">Q1 2026</span></div>
        <div className="pl"><span className="l">computed</span><span className="v">2 май 09:18</span></div>

        <div className="actions">
          <Link className="primary" to="/v2">→ открыть отчёт</Link>
          <button>⤓ экспорт</button>
        </div>
      </div>
    </div>
  );
}

export default function Overall() {
  const [t, setTweak] = useTweaks(mergeDefaults(TWEAK_DEFAULTS));
  const [period, setPeriod] = useState('Q1 2026');
  const initial: CellSelection = {
    rowIdx: 0, compId: 'dd',
    row: { ...PIVOT_ROWS[0], idx: 0 },
    comp: COMPANIES[0],
    val: PIVOT_ROWS[0].vals.dd[0],
  };
  const [selected, setSelected] = useState<CellSelection | null>(initial);

  const totalRow = PIVOT_ROWS.find((r) => r.total)!;
  const [tNetCalc] = totalsRow(totalRow);
  const sgdRow = PIVOT_ROWS[0];
  const [tSgdCalc] = totalsRow(sgdRow);
  const grossRow = PIVOT_ROWS.find((r) => /Валовая/.test(r.name))!;
  const [tGrossCalc] = totalsRow(grossRow);

  return (
    <div className="shell" data-theme={t.theme} data-density={t.density} data-variant={t.variant}>
      <NavRail />
      <div className="overall-page">
        <TitleBar />

        <div className="kpi-band" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
          <div className="kpi">
            <div className="lbl">СГД ХОЛДИНГ Q1</div>
            <div className="val">{fmt(tSgdCalc)}</div>
            <div className="sub">по 6 компаниям · KZT</div>
            {t.showSparklines && (
              <div className="spark">
                {[0.62, 0.71, 0.78, 0.83, 1.00].map((v, i) => (
                  <div key={i} className={`b ${i === 4 ? 'cur' : ''}`} style={{ height: v * 22 }} />
                ))}
              </div>
            )}
          </div>
          <div className="kpi">
            <div className="lbl">ВАЛОВАЯ ПРИБЫЛЬ</div>
            <div className="val">{fmt(tGrossCalc)}</div>
            <div className="sub">маржа {((tGrossCalc / tSgdCalc) * 100).toFixed(1)}%</div>
            {t.showSparklines && (
              <div className="spark">
                {[0.55, 0.62, 0.74, 0.82, 1.00].map((v, i) => (
                  <div key={i} className={`b ${i === 4 ? 'cur' : ''}`} style={{ height: v * 22 }} />
                ))}
              </div>
            )}
          </div>
          <div className="kpi">
            <div className="lbl">ЧИСТАЯ ПРИБЫЛЬ</div>
            <div className="val">{fmt(tNetCalc)}</div>
            <div className="sub">маржа {((tNetCalc / tSgdCalc) * 100).toFixed(1)}%</div>
            {t.showSparklines && (
              <div className="spark">
                {[0.45, 0.58, 0.72, 0.81, 1.00].map((v, i) => (
                  <div key={i} className={`b ${i === 4 ? 'cur' : ''}`} style={{ height: v * 22 }} />
                ))}
              </div>
            )}
          </div>
        </div>

        <Filters period={period} setPeriod={setPeriod} />

        <div className="overall-body">
          <PivotTable
            rows={PIVOT_ROWS} companies={COMPANIES}
            selected={selected} setSelected={setSelected}
          />
        </div>
      </div>

      <DetailOverlay sel={selected} onClose={() => setSelected(null)} />

      <TweaksPanel>
        <TweakSection label="Тема" />
        <TweakRadio label="Theme" value={t.theme}
          options={['paper', 'light', 'dark']}
          onChange={(v) => setTweak('theme', v)} />
        <TweakSection label="Вариант" />
        <TweakRadio label="Variant" value={t.variant}
          options={['safe', 'bold']}
          onChange={(v) => setTweak('variant', v)} />
        <TweakSection label="Плотность" />
        <TweakRadio label="Density" value={t.density}
          options={['compact', 'regular', 'comfy']}
          onChange={(v) => setTweak('density', v)} />
        <TweakSection label="Вид" />
        <TweakToggle label="Sparklines в KPI" value={t.showSparklines}
          onChange={(v) => setTweak('showSparklines', v)} />
      </TweaksPanel>
    </div>
  );
}
